// src/components/exercise/exercises/WritingExercise.tsx
import React, { useEffect, useState } from 'react';
import type { Exercise, ExerciseUserInput, GradeResult } from '../../../types/exercise';
import { QuestionBox } from './TrueFalseExercise';
import { useThemePalette } from '../../../hooks/useThemePalette';

interface Props {
  exercise: Exercise;
  onChange: (input: ExerciseUserInput) => void;
  isSubmitted: boolean;
  grade: GradeResult | null;
}

const WritingExercise: React.FC<Props> = ({ exercise, onChange, isSubmitted, grade }) => {
  const C = useThemePalette();
  const [value,   setValue]   = useState('');
  const [focused, setFocused] = useState(false);

  useEffect(() => { setValue(''); }, [exercise]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (isSubmitted) return;
    const v = e.target.value;
    setValue(v);
    onChange({ type: 'writing_comprehension', value: v });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <QuestionBox text={exercise.question} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <label style={{ fontSize: '0.68rem', fontWeight: 700, color: C.muted, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Your Answer
          </label>
          <span style={{ fontSize: '0.72rem', color: C.muted }}>
            Self-assessed — model answer shown after submit
          </span>
        </div>
        <textarea
          value={value}
          onChange={handleChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          disabled={isSubmitted}
          placeholder="Write your answer here…"
          rows={5}
          style={{
            padding: '14px 16px', borderRadius: '12px',
            border: `1.5px solid ${isSubmitted ? C.success : focused ? C.accent : C.border}`,
            background: isSubmitted ? 'rgba(52,211,153,0.04)' : C.card,
            color: C.text, fontSize: '0.95rem', lineHeight: 1.6,
            outline: 'none', resize: 'vertical',
            boxShadow: focused && !isSubmitted ? '0 0 0 3px rgba(79,140,247,0.12)' : 'none',
            transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
            width: '100%', boxSizing: 'border-box', fontFamily: 'inherit',
          }}
        />
      </div>

      {isSubmitted && grade && (
        <div style={{
          padding: '16px 20px', borderRadius: '14px',
          background: 'rgba(79,140,247,0.06)',
          border: '1px solid rgba(79,140,247,0.2)',
          display: 'flex', flexDirection: 'column', gap: '10px',
        }}>
          <div style={{
            fontWeight: 700, color: C.accent, fontSize: '0.7rem',
            letterSpacing: '0.1em', textTransform: 'uppercase',
          }}>
            Model Answer
          </div>
          <div style={{ color: C.text, fontSize: '0.95rem', lineHeight: 1.7 }}>
            {grade.correctAnswerDisplay || exercise.model_answer}
          </div>
          <div style={{ color: C.muted, fontSize: '0.78rem', borderTop: `1px solid ${C.border}`, paddingTop: '8px' }}>
            ✓ Marked correct — compare your response with the model answer above.
          </div>
        </div>
      )}
    </div>
  );
};

export default WritingExercise;
